import React, {createRef, Fragment, useState} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import __ from "lodash";
import axios from "axios";
import {Grid, InputAdornment, IconButton} from "@material-ui/core";
import { GetApp } from '@material-ui/icons';
import {MaskedText, DatePicker, FormDialog, FormSelect, FormCheckbox} from "../../../../controls";
import TripLegEditor from "./TripLegEditor";
import BuildPrimaryRouteFromLegs from '../../utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRoutesFromLegs from '../../utils/BuildOnwardRoutesFromLegs';
import BuildTruckRoutesFromLegs from '../../utils/BuildTruckRoutesFromLegs';
import Validator from "../../validators/AddTripValidator";
import useTripDepartmentUsers from "../../../../services/auth/useTripDepartmentUsers";

const AddTripDialog = (SystemData, apiSitePrefix, extraKeys, handleAdd, formatChars, classes, ownerDepartment) => {
  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;
  const tripOwnerDepartment = ownerDepartment;

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    title: 'Add Trip'
  });

  const tomorrow = moment.utc().add(1, 'day');
  const minDate = moment.utc().startOf('month').subtract(7, 'days');
  const maxDate = moment(minDate).add(3, 'year');
  const initialDate = tomorrow.toISOString();
  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [tripOwnerDepartment]});

  const open = (record) => {
    setDialogStates({...dialogStates, showDialog:true, record });
  }
  const close = () => {
    setDialogStates({...dialogStates, showDialog:false, record: {}});
  }

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const handleAddSector = () => {
    if (eventBus) {
      eventBus.trigger('Trip.AddSector');
    }
  }

  const handleLegsChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (changes && changes.record) {
      const legs = __.sortBy(changes.record.legs, ['contractedDepartureTimeUTC']);
      record['legs'] = legs;
      recordChange({...record, legs});

      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  const handleGetNextTripNo = (record, changeRecord) => {
    const {tripDate} = record;
    const year = moment(tripDate).format('YY');
    const month = moment(tripDate).format('MM');
    const requestNo = ++requestRef.current;
    axios.get(
      `${apiSitePrefix}/tripno/next/${year}/${month}`
    ).then(trip => {
      if (requestNo === requestRef.current) {
        if (trip.data) {
            changeRecord({...record, tripNo: trip.data});
        }
      }
    });
  }

  const Dialog = (props) => {
    if (dialogStates.showDialog) {
      return (
        <FormDialog
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          classes={classes}
          validator={Validator}
          buttons = {{
            addSector: {icon: "add_circle", key: "Add Sector", label: "Add Sector", color: "primary"},
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              const record = state.record;
              handleAdd.mutate({
                extraKeys,
                record,
                postSuccess: () => {
                  setDialogStates({...dialogStates, showDialog: false, record: null})
                  if (typeof props.onClose === 'function') {props.onClose()}
                },
                postError: (data) => {
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred.`);
                  }
                }
              });
            } else if (state.buttonPressed === "addSector") {
              handleAddSector();
            } else {
              setDialogStates({...dialogStates, showDialog: false, record: null});
              if (typeof props.onClose === 'function') {props.onClose()}
            }
          }}

        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={2} checkChildren={true}>
              <DatePicker
                id="tripDate"
                label="Trip Date"
                style={{width: "100%"}}
                minDate={minDate}
                maxDate={maxDate}
                defaultValue={initialDate}
                disableToolbar={true}
                autoOk={true}
                format={dateFormat}
                onChange={(value, props) => {
                  const {record, recordChange} = props;
                  const {tripDate, legs} = record;
                  const newTripDate = moment.utc(value);
                  if (!newTripDate.isValid()) return;
                  const diffDays = newTripDate.diff(tripDate, 'days');
                  for (let i = 0; i < legs.length; i++) {
                    const leg = legs[i];
                    let contractedDepartureTimeUTC = leg.contractedDepartureTimeUTC;
                    let contractedArrivalTimeUTC = leg.contractedArrivalTimeUTC;
                    if (!contractedArrivalTimeUTC) {
                      contractedArrivalTimeUTC = moment(tripDate).startOf('day').toISOString();
                    }
                    if (!contractedDepartureTimeUTC) {
                      contractedDepartureTimeUTC = moment(tripDate).startOf('day').toISOString();
                    }
                    leg.contractedDepartureTimeUTC = moment.utc(contractedDepartureTimeUTC).add(diffDays, 'days').toISOString();
                    leg.contractedArrivalTimeUTC = moment.utc(contractedArrivalTimeUTC).add(diffDays, 'days').toISOString();
                    leg.scheduledDepartureTimeUTC = leg.contractedDepartureTimeUTC;
                    leg.scheduledArrivalTimeUTC = leg.contractedArrivalTimeUTC;
                  }
                  recordChange({tripDate: value, legs});
                }}
              />
            </Grid>

            <Grid item xs={2} checkChildren={true}>
              {(record, changeRecord) => (
                <div>
                  <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                    Trip Number
                  </label>

                  <MaskedText
                    id="tripNo"
                    style={{width: "100%", marginTop: 2, display: "inline-block"}}
                    mask={`${process.env.REACT_APP_TRIP_PREFIX}${moment(moment(record.tripDate).isValid()?record.tripDate:undefined).format('YYMM')}___`}
                    maskChar={""}
                    alwaysShowMask={false}
                    formatChars={{'_':'[0-9]'}}
                    forceUpperCase={true}
                    defaultValue=""
                    inputLabelProps={{shrink: true}}
                    inputProps={{
                      endAdornment:(
                        <InputAdornment position="end">
                          <IconButton
                            style={{padding:0}}
                            aria-label="Get Next Available Trip No"
                            onClick={()=>{
                              handleGetNextTripNo(record, changeRecord);
                            }}
                          >
                            <GetApp/>
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </div>
              )}
            </Grid>
            {props.source !== "FLIGHT PLANNING"
              ?
                <Fragment checkChildren={true}>
                  <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                    {record =>
                      <FormSelect
                        id="commercialOwnerId"
                        label="Account Manager"
                        options={userOptions}
                        readOnly={false}
                        value={record.commercialOwnerId}
                        onChange={(value, props) => {
                          props.onChange(value);
                        }}
                        style={{width: 200}}
                      />
                    }
                  </Grid>
                  <Grid item xs={2} checkChildren={true}>
                    <MaskedText
                      id="uniqueCostingNo"
                      label="Costing Number"
                      style={{width: "100%"}}
                      mask="UUU-9999999"
                      maskChar={""}
                      alwaysShowMask={false}
                      formatChars={formatChars}
                      forceUpperCase={true}
                      defaultValue=""
                      inputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={1} checkChildren={true}>
                    {(record, changeRecord) => {
                      return (
                        <FormCheckbox
                          id="gsaTrip"
                          key="gsaTrip"
                          label="GSA Trip"
                          className={classes.checkbox}
                          checked={record.gsaTrip}
                        />
                      )
                    }}
                  </Grid>
                </Fragment>
              : <></>
            }
          </Grid>
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Route
                </label>
              </div>
              <div id="primaryRoute" style={{paddingTop: 6}}>
                {(rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="onwardRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Onward Routes
                </label>
              </div>
              <div id="onwardRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildOnwardRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
            <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="truckRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Trucking
                </label>
              </div>
              <div id="truckRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildTruckRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
            {(record, changeRecord) => (
              <TripLegEditor
                tripRecord={record}
                handleUpdate={handleAdd}
                extraKeys={extraKeys}
                canEdit={true}
                classes={classes}
                onChange={handleLegsChanged}
                canChangeContractedTimes={true}
              />
            )}
            </Grid>
          </Grid>
        </FormDialog>
      )
    }
    return (<></>);
  }
  return {
    Dialog,
    open,
    close
  }

}

export default AddTripDialog;
